import {
    Injectable, inject
} from "@angular/core";
import {
    ModelAction, ISearchSort
} from "@shopliftr/common-js/data";
import {
    BatchApiResponse, BulkResponse, BulkUtil, CreateRequest, DeleteRequest,
    DeleteResponse, GetResponse, RequestUtil, SearchResponse, Store,
    StoreExportSubmission, StoreSearchFilterOptions, UpdateRequest
} from "@shopliftr/common-js/shared";
import {
    IFullAccessInternalApiClientService
} from "../../../../interfaces/internal-api-client";
import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";
import { ListUtil } from "../../../../list/util/list-util";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

/**
 * API client service for managing store CRUD operations through internal API endpoints.
 *
 * Implements the API Client Service pattern providing full CRUD access for the Store
 * model via the Internal API v2 `/v2/store` routes. This service encapsulates all
 * store endpoint paths, builds typed requests using RequestUtil, and maps responses
 * to Store domain models.
 *
 * **Service Pattern:** API Client Service
 *
 * **Responsibilities:**
 * - Encapsulates store API endpoint paths
 * - Builds typed requests using RequestUtil
 * - Maps responses to Store domain models
 * - Provides full CRUD operations: create, bulkCreate, get, bulkGet, getAll,
 *   search, update, bulkUpdate, updateWithChanges, delete, bulkDelete
 *
 * **Provision:** Singleton service provided in 'root'
 *
 * @pattern IFullAccessInternalApiClientService
 * @providedIn root
 * @implements {IFullAccessInternalApiClientService<Store>} Full CRUD access for Store model
 *
 * @example
 * ```typescript
 * // Inject the service
 * private readonly _storeApiClientService = inject(StoreApiClientService);
 *
 * // Get a store
 * this._storeApiClientService.get$('store-id').subscribe(store => {
 *   console.log(store.name);
 * });
 *
 * // Search stores
 * const filterOptions = new StoreSearchFilterOptions();
 * this._storeApiClientService.search$(filterOptions, 0, 10).subscribe(response => {
 *   console.log(response.results);
 * });
 * ```
 *
 * @see {@link IFullAccessInternalApiClientService} for the full-access API client interface
 */
@Injectable({ providedIn: "root" })
export class StoreApiClientService implements IFullAccessInternalApiClientService<Store> {

    /**
     * Base API path for store endpoints.
     * Used as the root path segment for constructing API URLs.
     *
     * @readonly
     */
    readonly basePath = "store";

    /**
     * Internal API request service for making HTTP calls to the backend.
     * Injected using Angular's inject() function.
     *
     * @private
     * @readonly
     */
    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieves multiple stores by their unique identifiers in a single bulk request.
     *
     * @param ids - Array of unique identifiers of the stores to retrieve
     * @returns Observable that emits a BulkResponse containing the requested stores
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<Store>(id, Store.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieves a single store by its unique identifier.
     *
     * @param id - The unique identifier of the store to retrieve
     * @returns Observable that emits the requested Store entity
     */
    get$(id: string): Observable<Store> {
        const request = RequestUtil.createGetRequest<Store>(id, Store.modelName, ModelAction.ClientGet);
        return this._internalApiRequestService.get$(request, this.basePath).pipe(
            map((response: GetResponse<Store>) => response.payload)
        );
    }

    /**
     * Retrieves all stores matching the specified filter criteria without pagination.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param sort - Optional sorting configuration
     * @returns Observable that emits an array of all matching Store entities
     */
    getAll$(filterOptions: StoreSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<Store>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch, Store.modelName, undefined, undefined, filterOptions, sort
        );
        return this._internalApiRequestService.getAll$<Store, StoreSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Performs a paginated search for stores matching the specified criteria.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param page - Zero-based page number for pagination (optional)
     * @param pageSize - Number of results per page (optional)
     * @param sort - Optional sorting configuration
     * @returns Observable that emits a SearchResponse containing matching stores and pagination metadata
     */
    search$(
        filterOptions: StoreSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<Store>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Store.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.search$<Store, StoreSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Creates a new store in the system.
     *
     * @param fields - The store data to create
     * @returns Observable that emits the created store with assigned ID and metadata
     */
    create$(fields: Store): Observable<Store> {
        const createRequest = RequestUtil.createCreateRequest<Store, void>(
            ModelAction.ClientCreate, fields.cloneForAction(ModelAction.ClientCreate)
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath).pipe(
            map((response) => response.payload)
        );
    }

    /**
     * Creates multiple stores in a single bulk request.
     *
     * @param items - Array of create requests for stores to create
     * @returns Observable that emits a BulkResponse with the results of each create operation
     */
    bulkCreate$(items: Array<CreateRequest<Store, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Updates an existing store with new field values or removes specified fields.
     *
     * @param id - The unique identifier of the store to update
     * @param version - The current version of the store for optimistic locking
     * @param fields - The store fields to update with new values
     * @param removeFields - Array of field names to remove from the store (optional)
     * @returns Observable that emits the updated store
     */
    update$(id: string, version: number, fields: Store, removeFields?: Array<string>): Observable<Store> {
        const updateFields = fields.cloneForAction(ModelAction.ClientUpdate);
        const updateRequest: UpdateRequest<Store> = RequestUtil.createUpdateRequest(
            id, ModelAction.ClientUpdate, updateFields, version, removeFields
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath).pipe(
            map((response) => response.payload)
        );
    }

    /**
     * Updates the record of the original store with the updated model provided.
     *
     * @param original - The original store model
     * @param updated - The updated store model with changes applied
     * @returns Observable that emits the updated store
     */
    updateWithChanges$(original: Store, updated: Store): Observable<Store> {
        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }

    /**
     * Updates multiple stores in a single bulk request.
     *
     * @param requests - Array of update requests for stores to update
     * @returns Observable that emits a BulkResponse with the results of each update operation
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Deletes a store by its unique identifier and version.
     *
     * @param id - The unique identifier of the store to delete
     * @param version - The current version of the store for optimistic locking
     * @returns Observable that emits a DeleteResponse on successful deletion
     */
    delete$(id: string, version: number): Observable<DeleteResponse> {
        const deleteRequest = RequestUtil.createDeleteRequest<Store>(
            id, Store.modelName, ModelAction.ClientDelete, version
        );
        return this._internalApiRequestService.delete$(deleteRequest, this.basePath);
    }

    /**
     * Deletes multiple stores in a single bulk request.
     *
     * @param items - Array of delete requests for stores to delete
     * @returns Observable that emits a BulkResponse with the results of each delete operation
     */
    bulkDelete$(items: Array<DeleteRequest<unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Requests an emailed store export for the submission's chains.
     *
     * internal-api forwards the request to the Batch Engine, which emails the export to
     * every recipient asynchronously.
     *
     * @param submission - Chains, recipients and include options for the export
     * @returns Observable that emits the Batch Engine's confirmation
     */
    submitExport$(submission: StoreExportSubmission): Observable<BatchApiResponse> {
        const request = RequestUtil.createSubmitRequest(submission);
        return this._internalApiRequestService
            .submit$<StoreExportSubmission, BatchApiResponse>(request, `${this.basePath}/export`)
            .pipe(map((response) => response.payload));
    }
}
